import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog } from 'radix-ui'
import { Button } from '@/components/ui/Button'
import { Icon } from '@/components/ui/Icon'
import { describeError } from '@/components/ui/error-copy'
import { useAcceptTerms } from '@/features/terms/api'
import { termsSections, termsVersion } from '@/features/terms/content'
import { isApiError } from '@/lib/api/problem'
import { cn } from '@/lib/cn'

type TermsDialogProps = {
  open: boolean
  notice?: string
  onAccepted: () => void
  onDeclined: () => void
}

export function TermsDialog({ open, notice, onAccepted, onDeclined }: TermsDialogProps) {
  const { t } = useTranslation()
  const accept = useAcceptTerms()
  const [error, setError] = useState<string | null>(null)

  const decline = () => {
    setError(null)
    accept.reset()
    onDeclined()
  }

  const agree = () => {
    setError(null)
    accept.mutate(termsVersion, {
      onSuccess: () => {
        onAccepted()
      },
      onError: (failure) => {
        setError(
          isApiError(failure) && failure.isTermsVersionMismatch
            ? t('terms.outdated')
            : t(describeError(failure).description),
        )
      },
    })
  }

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          decline()
        }
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-ink/40" />
        <Dialog.Content
          className={cn(
            'fixed top-1/2 left-1/2 z-50 flex max-h-[calc(100dvh-2rem)] w-[calc(100vw-2rem)] max-w-2xl',
            '-translate-x-1/2 -translate-y-1/2 flex-col',
            'panel rounded-lg p-6 shadow-lg',
            'focus-visible:outline-none',
          )}
          onPointerDownOutside={(event) => {
            event.preventDefault()
          }}
        >
          <div className="flex items-start justify-between gap-4">
            <Dialog.Title className="text-xl font-semibold text-ink">
              {t('terms.title')}
            </Dialog.Title>
            <Dialog.Close
              aria-label={t('common.close')}
              className={cn(
                'inline-flex size-control-sm shrink-0 items-center justify-center rounded-md text-ink-muted',
                'transition-colors duration-250 ease-brand hover:bg-surface-hover hover:text-ink',
                'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
              )}
            >
              <Icon name="close" />
            </Dialog.Close>
          </div>

          <Dialog.Description className="mt-2 text-sm text-ink-muted">
            {t('terms.description', { version: termsVersion })}
          </Dialog.Description>

          {notice === undefined ? null : (
            <p className="mt-4 flex items-start gap-2 rounded-md border border-warning/40 bg-warning-surface p-3 text-sm text-ink">
              <Icon name="alertTriangle" className="mt-0.5 size-4 shrink-0 text-warning-strong" />
              {notice}
            </p>
          )}

          <article
            tabIndex={0}
            aria-label={t('terms.documentLabel')}
            className="mt-4 min-h-0 flex-1 overflow-y-auto rounded-md border border-border bg-surface-sunken p-4 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
          >
            <div className="flex flex-col gap-4">
              {termsSections().map((section) => (
                <section key={section.title} className="flex flex-col gap-2">
                  <h3 className="text-base font-semibold text-ink">{section.title}</h3>
                  {section.paragraphs.map((paragraph) => (
                    <p key={paragraph} className="text-sm text-ink-secondary">
                      {paragraph}
                    </p>
                  ))}
                </section>
              ))}
            </div>
          </article>

          {error === null ? null : (
            <p role="alert" className="mt-3 text-sm text-error-strong">
              {error}
            </p>
          )}

          <p className="mt-4 text-sm text-ink-muted">{t('terms.declineHint')}</p>

          <div className="mt-4 flex flex-wrap justify-end gap-2">
            <Button variant="secondary" onClick={decline} disabled={accept.isPending}>
              {t('terms.decline')}
            </Button>
            <Button icon="check" loading={accept.isPending} onClick={agree}>
              {t('terms.accept')}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
